import React, { EventHandler, Fragment, KeyboardEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AgentVerificationBulkRejectModal } from '../agentVerificationBulkRejectModal/AgentVerificationBulkRejectModal'

import { ErrorView, SearchAndDateFilter } from '@percent/admin-dashboard/common/components'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import { Button, Flex, TabItemProps, Tabs } from '@percent/lemonade'
import {
  AgentVerificationState,
  AgentVerificationStateFilter,
  AgentVerificationTableProps
} from './AgentVerificationTable.types'
import { DefaultAvTable } from './tables/DefaultAvTable'
import { ExternalActionableAvTable } from './tables/ExternalActionableAvTable'
import { DocumentReviewAvTable } from './tables/DocumentReviewAvTable'
import { PercentActionableAvTable } from './tables/PercentActionableAvTable'

const agentVerificationStates = Object.keys(AgentVerificationStateFilter).filter(key =>
  Number.isNaN(Number(key))
) as AgentVerificationState[]

const getTabTableByState = (state: AgentVerificationState) => {
  switch (state) {
    case 'external_actionable':
      return ExternalActionableAvTable
    case 'additional_document_review_needed':
      return DocumentReviewAvTable
    case 'percent_actionable':
      return PercentActionableAvTable
    default:
      return DefaultAvTable
  }
}

export function AgentVerificationTable({
  isLoading,
  validations,
  totalResults,
  nextPage,
  previousPage,
  errorMessage,
  queryParams,
  setQueryParams
}: AgentVerificationTableProps) {
  const { t } = useTranslation()
  const { userHasPermissions } = usePermissions()
  const [isBulkRejectModalOpen, setIsBulkRejectModalOpen] = useState(false)

  const canBulkReject = userHasPermissions('agent_verifications_bulk_rejection:can_bulk_reject')

  const agentVerificationState: AgentVerificationState = agentVerificationStates.includes(
    queryParams.state as unknown as AgentVerificationState
  )
    ? (queryParams.state as AgentVerificationState)
    : 'all'

  const tabs = useMemo<TabItemProps<AgentVerificationState>[]>(
    () => [
      { key: 'all', label: t('table.all') },
      { key: 'percent_actionable', label: t('table.percentActionNeeded') },
      { key: 'additional_document_review_needed', label: t('table.additionalDocumentReviewNeeded') },
      { key: 'external_actionable', label: t('table.externalActionNeeded') },
      { key: 'approved', label: t('table.accepted') },
      { key: 'rejected', label: t('table.rejected') }
    ],
    [t]
  )

  const handleTabChange = (_tab: string) => {
    const tab = _tab as AgentVerificationState
    const state = tab !== 'all' ? tab : undefined

    if (state === 'percent_actionable') {
      setQueryParams({
        state,
        sort: 'createdAt',
        direction: 'ASC'
      })
    } else if (state === 'additional_document_review_needed') {
      setQueryParams({
        state,
        sort: 'documentUploadedAt',
        direction: 'ASC'
      })
    } else {
      setQueryParams({ state, sort: undefined, direction: undefined })
    }
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        setQueryParams({
          query: (event.target as HTMLInputElement).value
        })
      }
    },
    [setQueryParams]
  )

  const AvTable = getTabTableByState(agentVerificationState)

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <Fragment key={agentVerificationState}>
      <AvTable
        isLoading={isLoading}
        totalResults={totalResults}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        validations={validations}
        nextPage={nextPage}
        previousPage={previousPage}
      >
        <Tabs activeTab={agentVerificationState} items={tabs} onChangeTab={handleTabChange} />
        <Flex w="100%">
          <SearchAndDateFilter
            handleKeyPress={handleKeyPress}
            searchPhrase={queryParams.query ?? ''}
            placeholder={t('table.adminAVSearchPlaceholder')}
            setQueryParams={setQueryParams}
            actions={
              canBulkReject && (
                <Button
                  size="small"
                  variant="primary"
                  onClick={() => setIsBulkRejectModalOpen(true)}
                  data-testid="av-bulk-reject-button"
                >
                  {t('button.bulkRejectAgentVerifications')}
                </Button>
              )
            }
          />
        </Flex>
      </AvTable>
      {isBulkRejectModalOpen && <AgentVerificationBulkRejectModal onClose={() => setIsBulkRejectModalOpen(false)} />}
    </Fragment>
  )
}
